import { defaultTheme } from '@helpscout/hsds-tokens'
import { getToken } from '@helpscout/hsds-tokens/getToken'
import { createGlobalStyle } from 'styled-components'

import { HSDS_SCOPE } from './Provider'

export const BASE_FONT_SIZE = 13

const themes = { default: defaultTheme }

export type ThemeName = keyof typeof themes

const scopeSelector = `.${HSDS_SCOPE}`
const scopeSelectorHsApp = `.${HSDS_SCOPE}-hs-app`
const hsAppResetCSS = `
  /* reset element-level styling from hs-app */
  a,
  abbr,
  address,
  blockquote,
  body,
  button,
  code,
  dd,
  dl,
  dt,
  form,
  figure,
  fieldset,
  input,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  label,
  legend,
  li,
  ol,
  pre,
  table,
  textarea,
  ul {
    margin: initial;
    padding: initial;
    height: initial;
    width: initial;
    line-height: initial;
    border: 0;
    background-color: initial;
  }

  #sidebar-lt.reset {
    padding: initial !important;
  }

  .c-app-layout__sidebar.reset {
    width: auto !important;
  }
`

const globalHSDSTokens = `
  --HSDSGlobalFontFamily: ${getToken('font.family')};
  --HSDSGlobalFontFamilySystem: ${getToken('font.system')};
  --HSDSGlobalFontFamilyMono: ${getToken('font.mono')};
  --HSDSGlobalFontSize: ${getToken('font.size')};
  --HSDSGlobalBackgroundColor: ${getToken('color.background.surface')};
  --HSDSGlobalColor: ${getToken('color.text.default')};
`

function getDataTheme(themeName: ThemeName) {
  if (!themes[themeName]) return ''
  return `
    ${themes[themeName].tokens};
  `
}

/**
 * Inject global HSDS tokens into the document.
 * Called at module load time to prevent FOUC, and also exported so the Provider
 * can call it during render as a safety net (e.g., iframe DOM resets in Storybook
 * can destroy the style tag while the JS module cache persists).
 *
 * Idempotent — deduplication is handled by the style tag's ID.
 */
export function injectGlobalTokens() {
  if (typeof document === 'undefined' || !document.head) return
  // JSDOM chokes on too many CSS variables
  if (
    typeof navigator !== 'undefined' &&
    navigator?.userAgent?.includes('jsdom')
  )
    return
  if (document.getElementById('hsds-design-tokens')) return

  const style = document.createElement('style')
  style.id = 'hsds-design-tokens'
  style.textContent = `
    ${hsAppResetCSS}

    :root {
      ${globalHSDSTokens};
      ${getDataTheme('default')};
    }

    body {
      background: var(--HSDSGlobalBackgroundColor);
      color: var(--HSDSGlobalColor);
      font-family: var(--HSDSGlobalFontFamily);
      font-size: var(--HSDSGlobalFontSize);
    }
  `
  document.head.appendChild(style)
}

injectGlobalTokens()

function getNonHsdsScopeStyles({
  themeName,
  scope,
}: {
  themeName: ThemeName
  scope: string
}) {
  return `
    ${scope} {
      ${globalHSDSTokens};
      ${getDataTheme(themeName)};
    }
  `
}

export default createGlobalStyle<{
  themeName: ThemeName
  scope: string
}>`
  ${({ themeName, scope }) => {
    const isCustomScope =
      scope !== scopeSelector && scope !== scopeSelectorHsApp

    return `
    ${scope ? scope : scopeSelector} {
      *, *::before, *::after {
        box-sizing: border-box;
      }
    }

    ${isCustomScope ? getNonHsdsScopeStyles({ themeName, scope }) : ''}
    `
  }}
`
